import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { useCallback, useMemo } from 'react';
import checkBrowserSupport, {
  supportedBrowserMapVideo,
} from 'shared/components/UnsupportedBrowserPopover/checkBrowserSupport';
import { BrowserSupportFeatures } from 'shared/enums/BrowserSupportFeatures';
import { PopoverName } from 'shared/enums/PopoverName';
import { usePopover } from 'shared/providers/PopoverWindowProvider';
import styled from 'styled-components/macro';

interface IVideoCallAttachmentProps {
  disabled: boolean;
  channelId: string;
  clinicPetParentId: string;
}

const CallRequestAttachment = ({ disabled, channelId, clinicPetParentId }: IVideoCallAttachmentProps): JSX.Element => {
  const { openPopover } = usePopover();
  const toast = useToast();

  const { isBrowserSupported, OS, browser } = useMemo(() => {
    return checkBrowserSupport(BrowserSupportFeatures.Video);
  }, []);

  const joinCall = useCallback((): void => {
    const startLeft = window.innerWidth / 4;
    const startTop = window.innerHeight / 4;
    openPopover({ name: PopoverName.VideoCall, startTop, startLeft, data: { channelId, clinicPetParentId } });
  }, [channelId, openPopover, clinicPetParentId]);

  const handleCopyUrl = (): void => {
    navigator.clipboard.writeText(window.location.href).then(
      () => {
        toast({
          title: 'Your conversation URL has been successfully copied to the clipboard',
          status: 'success',
        });
      },
      (e) => {
        console.warn(e);
        toast({
          title:
            "Your conversation URL was not able to be copied to the clipboard. Please copy the URL from your browser's search bar.",
          status: 'error',
        });
      },
    );
  };

  return (
    <VideoCallRequestDiv isBrowserSupported={isBrowserSupported}>
      <Text variant="subtle">Video Call Requested</Text>
      {isBrowserSupported ? (
        <Button my={2} iconName="videoCamera" size="sm" disabled={disabled} onClick={joinCall}>
          Join Now
        </Button>
      ) : (
        <UnsupportedBrowser>
          {Object.keys(supportedBrowserMapVideo[OS] || []).length ? (
            <div>
              Unfortunately, video calling is not supported with {browser ? `the ${browser}` : 'your current browser'}{' '}
              and {OS && `${OS} `} operating system combination. <b>Please copy the link below</b> and paste it into one
              of the following browsers on your {OS || ''} device to join the video call:
              <ul>
                {Object.keys(supportedBrowserMapVideo[OS]).map((browserName) => {
                  return (
                    <li key={browserName}>
                      <b>{browserName}</b>
                    </li>
                  );
                })}
              </ul>
              <VideoCallLink onClick={handleCopyUrl}>Click here to copy conversation URL</VideoCallLink>
            </div>
          ) : (
            <div>
              There are no supported browsers for this operating system. The supported operating systems include
              Windows, Android, Mac OS, iOS, and Linux.
            </div>
          )}
        </UnsupportedBrowser>
      )}
    </VideoCallRequestDiv>
  );
};

export default CallRequestAttachment;

interface IVideoCallRequest {
  isBrowserSupported: boolean;
}

const VideoCallRequestDiv = styled.div<IVideoCallRequest>`
  max-width: ${({ isBrowserSupported }): number => (isBrowserSupported ? 170 : 350)}px;
`;

const UnsupportedBrowser = styled.div`
  padding: 10px 24px;
`;

const VideoCallLink = styled.div`
  color: hsl(180deg 100% 30%);
  text-decoration: underline;
  padding: 10px 0;
  cursor: pointer;
`;
